import { strings } from '../strings';

/** One shortcut for the help dialog and the palette's key hints (spec 6.1.12). */
export interface Shortcut {
  id: string;
  /** Alternatives; each is a list of keys pressed together (or in order, for `sequence`). */
  keys: string[][];
  sequence?: boolean;
  label: string;
}

const s = strings.shell.shortcuts;

export const isMac = () => typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);

/** "Ctrl" on Windows and Linux, "⌘" on macOS. */
export const ctrlKey = () => (isMac() ? '⌘' : 'Ctrl');

/** `networking`: `g n` is listed only while networking is on (feature spec 17). */
export function shellShortcuts(networking = false): Shortcut[] {
  const ctrl = ctrlKey();
  return [
    { id: 'palette', keys: [[ctrl, 'K']], label: s.palette },
    { id: 'help', keys: [['?']], label: s.help },
    { id: 'go-blueprints', keys: [['G', 'B']], sequence: true, label: s.goBlueprints },
    { id: 'go-deployments', keys: [['G', 'D']], sequence: true, label: s.goDeployments },
    ...(networking
      ? [
          {
            id: 'go-networks',
            keys: [['G', 'N']],
            sequence: true,
            label: strings.networks.nav.goNetworks,
          },
        ]
      : []),
    { id: 'go-runs', keys: [['G', 'R']], sequence: true, label: s.goRuns },
    { id: 'go-cloud-accounts', keys: [['G', 'C']], sequence: true, label: s.goCloudAccounts },
    { id: 'focus-search', keys: [['/']], label: s.focusSearch },
    { id: 'next-region', keys: [['F6']], label: s.nextRegion },
    { id: 'previous-region', keys: [['Shift', 'F6']], label: s.previousRegion },
    { id: 'close', keys: [['Esc']], label: s.close },
  ];
}

export function designerShortcuts(): Shortcut[] {
  const ctrl = ctrlKey();
  return [
    { id: 'save', keys: [[ctrl, 'S']], label: s.save },
    { id: 'undo', keys: [[ctrl, 'Z']], label: s.undo },
    {
      id: 'redo',
      keys: [
        [ctrl, 'Shift', 'Z'],
        [ctrl, 'Y'],
      ],
      label: s.redo,
    },
    { id: 'node-focus', keys: [['Tab']], label: s.nodeFocus },
    { id: 'node-move', keys: [[s.arrowKeys]], label: s.nodeMove },
    { id: 'node-delete', keys: [['Delete']], label: s.nodeDelete },
    { id: 'node-inspect', keys: [['Enter']], label: s.nodeInspect },
  ];
}

/** The tool-window shortcuts (spec 6.8.6), with a toggle per registered window. */
export function toolWindowShortcuts(
  windows: readonly { id: string; title: string; shortcut: number }[],
): Shortcut[] {
  const ctrl = ctrlKey();
  const t = strings.dock.shortcuts;
  return [
    ...windows.map((w) => ({
      id: `tool-window-${w.id}`,
      keys: [[ctrl, 'Shift', String(w.shortcut)]],
      label: t.toggle(w.title),
    })),
    { id: 'tool-window-hide', keys: [['Shift', 'Esc']], label: t.hide },
    { id: 'tool-window-maximize', keys: [[ctrl, 'Shift', 'Enter']], label: t.maximize },
    { id: 'tool-window-dock', keys: [[ctrl, 'Shift', s.arrowKeys]], label: t.dock },
  ];
}

export function shortcutKeys(id: string): string[] | undefined {
  return shellShortcuts().find((shortcut) => shortcut.id === id)?.keys[0];
}

/** Focus is where the user types: page shortcuts must not fire (spec 6.1.12). */
export function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  if (target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement) return true;
  if (target instanceof HTMLInputElement) {
    return !['button', 'checkbox', 'radio', 'submit', 'reset', 'range', 'color', 'file'].includes(
      target.type,
    );
  }
  return target.getAttribute('role') === 'combobox' || target.getAttribute('role') === 'textbox';
}

/**
 * The F6 regions in order (spec 6.1.12): top bar, rail, page, the dock zones, the floating windows (bottom to
 * top), status bar. A maximized tool window takes the page's place; regions inside `inert` are skipped.
 */
export const REGION_ORDER = [
  'topbar',
  'rail',
  'main',
  'maximized',
  'dock-left',
  'dock-right',
  'dock-bottom',
  'floating',
  'status',
] as const;

function regionRank(element: HTMLElement): number {
  const rank = (REGION_ORDER as readonly string[]).indexOf(element.dataset.region ?? '');
  return (rank < 0 ? REGION_ORDER.length : rank) * 1000 + Number(element.dataset.stack ?? 0);
}

export function focusRegion(direction: 1 | -1, root: Document = document): void {
  const regions = Array.from(root.querySelectorAll<HTMLElement>('[data-region]'))
    .filter((region) => !region.closest('[inert]'))
    .map((region, index) => ({ region, index, rank: regionRank(region) }))
    .sort((a, b) => a.rank - b.rank || a.index - b.index)
    .map(({ region }) => region);
  if (regions.length === 0) return;
  const active = root.activeElement;
  // The innermost region holding the focus.
  let current = -1;
  regions.forEach((region, index) => {
    if (region === active || region.contains(active)) current = index;
  });
  const next =
    current < 0
      ? direction === 1
        ? 0
        : regions.length - 1
      : (current + direction + regions.length) % regions.length;
  regions[next].focus();
}

/** Focuses the search field of the current list (`/`, spec 6.1.12); false when the page has none. */
export function focusListSearch(root: Document = document): boolean {
  const field = root.querySelector<HTMLElement>('[data-list-search]');
  if (!field) return false;
  field.focus();
  return true;
}

/** A modal dialog (other than the palette's own) is open: shell shortcuts stay quiet. */
export function modalOpen(root: Document = document): boolean {
  return root.querySelector('[aria-modal="true"]') !== null;
}
